import { MouseEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import Popover from '@/components/Popover';
import styled from '@/styles';

const Wrap = styled('span', {
  position: 'relative',
  display: 'inline-flex',
  marginLeft: '4px',
});
const Button = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
  color: '#5A5A5A',
  display: 'inline-flex',
  '&:focus-visible': { outline: '2px solid #2169BA', borderRadius: '50%' },
});
// GRAVY-10834: sized per the design (Figma "GravyWork 2.0 Baseline"):
// the tiers panel (38010:24777) hugs four 80px columns inside 16/20px
// padding with a 16px title; the shifts panel (38010:25296) is 231px wide
// with 12px text. Both share the #FCFCFC surface, 1px #DADADA border and
// the 0 4px 6px shadow.
const Surface = styled('div', {
  padding: '16px 20px',
  backgroundColor: '#FCFCFC',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.12)',
  textAlign: 'left',
  textTransform: 'none',
  letterSpacing: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: 1.4,
  whiteSpace: 'normal',
  color: '#1C1C1C',
  '& p': { margin: '0 0 8px' },
  '& p:last-child': { marginBottom: 0 },
  variants: {
    kind: {
      tiers: { width: '360px' },
      shifts: { width: '231px' },
    },
  },
});
const Title = styled('div', {
  fontWeight: 700,
  color: '#1C1C1C',
  variants: {
    kind: {
      tiers: { fontSize: '16px', marginBottom: '20px' },
      shifts: { fontSize: '12px', fontWeight: 800, marginBottom: '12px' },
    },
  },
});

const InfoGlyph = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10 9v5M10 6.5v.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

export type HeaderTooltipKind = 'tiers' | 'shifts';

type Props = {
  testId: string;
  label: string;
  title: string;
  kind?: HeaderTooltipKind;
  children: ReactNode;
};

// Every mounted tooltip registers its own close function here so that opening
// one can dismiss the others (GRAVY-10793). The trigger calls stopPropagation()
// to keep the click off the sortable column header, which also stops the native
// event before it reaches window — where Popover's useClickout listens — so the
// open sibling never got its clickout and both popovers stayed on screen.
const openCloseHandlers = new Set<() => void>();

const closeOtherTooltips = (mine: () => void) => {
  openCloseHandlers.forEach((closeOther) => {
    if (closeOther !== mine) closeOther();
  });
};

// Escape dismissal is already handled by Popover's own window keydown listener,
// so this component does not add a second one.
const HeaderTooltip = ({
  testId,
  label,
  title,
  kind = 'shifts',
  children,
}: Props) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    openCloseHandlers.add(close);

    return () => {
      openCloseHandlers.delete(close);
    };
  }, [close]);

  return (
    <Wrap>
      <Button
        type="button"
        data-testid={testId}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          if (!open) closeOtherTooltips(close);
          setOpen(!open);
        }}
      >
        <InfoGlyph />
      </Button>
      {open && (
        <Popover
          isOpen={open}
          onRequestClose={close}
          align="above"
          justify="right"
        >
          <Surface
            role="dialog"
            aria-label={title}
            kind={kind}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <Title kind={kind}>{title}</Title>
            {children}
          </Surface>
        </Popover>
      )}
    </Wrap>
  );
};

export default HeaderTooltip;
